import { useRef, useState, useCallback, useEffect, RefObject } from 'react';

export interface UsePopoverReturn {
  open: boolean;
  openAbove: boolean;
  triggerRef: RefObject<HTMLButtonElement>;
  popoverRef: RefObject<HTMLDivElement>;
  toggle: () => void;
  close: () => void;
}

/**
 * Manages open/close state for a popover attached to a trigger button.
 *
 * - `openAbove` is computed when opening: true when < 240px remain below the trigger.
 * - Outside-click (mousedown) and Escape keydown listeners are added only while open,
 *   and cleaned up immediately on close.
 */
export const usePopover = (): UsePopoverReturn => {
  const [open, setOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen(prev => {
      if (prev) {
        return false;
      }
      // Compute openAbove when opening
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenAbove(spaceBelow < 240);
      }
      return true;
    });
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (
      triggerRef.current?.contains(e.target as Node) ||
      popoverRef.current?.contains(e.target as Node)
    ) {
      return;
    }
    setOpen(false);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  // Add/remove listeners only while the popover is open
  useEffect(() => {
    if (!open) return;

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleMouseDown, handleKeyDown]);

  return { open, openAbove, triggerRef, popoverRef, toggle, close };
};
